import { LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client/react';
import { Form } from 'antd';
import React, { useState } from 'react';
import { botMessagesTypes } from '@guuru/chat-web';
import { SendButtonOutlined } from '@guuru/react-icon';
import { i18n } from '@guuru/translation-web';
import { FormItem, SearchInput } from '../../SearchInput';
import ChatRenderText from '../Text';
import PROVIDE_USER_EMAIL from './mutations/ProvideUserEmail';
import GET_USER_INFO from './queries/GetUserEmail';

interface Message {
  metadata?: {
    isHotTransferMessage?: boolean;
  };
}

const isHotTransfer = (message: Message) =>
  !!message?.metadata?.isHotTransferMessage;

const translateEmailProvided = (message: Message, email: string) => {
  const translationCode = isHotTransfer(message)
    ? 'bot:::hotTransferEmailProvided'
    : botMessagesTypes.requestEmailThanks;

  return i18n.t(translationCode, { email });
};

interface ChatRenderUserInfoProps {
  chatId: string;
  message: React.ComponentProps<typeof ChatRenderText>['message'] & {
    metadata: {
      email?: string;
      isHotTransferMessage?: boolean;
    };
  };
  readOnly?: boolean;
}

const ChatRenderUserInfo = function ({
  chatId,
  message,
  readOnly = true,
}: ChatRenderUserInfoProps) {
  const [form] = Form.useForm();
  const [userEmail, setUserEmail] = useState<string>();

  const { loading, data } = useQuery(GET_USER_INFO, {
    variables: { id: chatId },
  });

  const user = data?.chat?.user;

  const [provideUserEmail, { loading: isSaving }] = useMutation(
    PROVIDE_USER_EMAIL,
    {},
  );

  const invalidEmail = i18n.t('bot:::messageTypeContactValidationEmail');
  const placeholderEmail = i18n.t('bot:::messageTypeContactMessageEmail');

  const newMessage = userEmail
    ? { ...message, i18n: translateEmailProvided(message, userEmail) }
    : message;

  const handleSubmit = async ({ email: submittedEmail }: { email: string }) => {
    try {
      await provideUserEmail({
        variables: {
          chatId,
          email: submittedEmail,
          isHotTransfer: isHotTransfer(message),
        },
      });
      setUserEmail(submittedEmail);
    } catch (_) {
      form.setFields([{ name: 'email', errors: [invalidEmail] }]);
    }
  };

  const Icon = isSaving ? LoadingOutlined : SendButtonOutlined;

  return (
    <ChatRenderText message={newMessage} tooltip={false} loading={loading}>
      {!readOnly && !user?.email && !userEmail && (
        <Form className="message-options" onFinish={handleSubmit} form={form}>
          <FormItem
            name="email"
            validateTrigger="onSubmit"
            rules={[
              {
                message: invalidEmail,
                type: 'email',
                required: true,
              },
            ]}
          >
            <SearchInput
              type="email"
              placeholder={placeholderEmail}
              enterButton={<Icon />}
              onSearch={form.submit}
              onPressEnter={() => {}}
              disabled={isSaving}
            />
          </FormItem>
        </Form>
      )}
    </ChatRenderText>
  );
};

export default ChatRenderUserInfo;
